import React, { useCallback, useMemo } from 'react'
import Grid from '@mui/material/Grid'
import clsx from 'clsx'
import _slice from 'lodash/slice'
import _toNumber from 'lodash/toNumber'
import Typography from 'components/Typography'
import { name as nameConstant } from 'appConstants'
import { Collection } from 'models/ApiModels'
import { emptyFunction } from 'utils'
import { UrlUtils } from 'utils/TextUtils'
import { dialogActions, InformationDialog } from 'duck/AppDuck/DialogDuck'
import { apiActions } from 'duck/ApiDuck/actions'
import CollectionCardContent from './CollectionCardContent'

import styles from './CollectionCard.module.scss'

const EMPTY_TAG_ARRAY: string[] = []

export type CollectionCardProps = {
  collection: Collection
  to?: string
  onClick?: Function
  variant?: 'light' | undefined
  currentUserId: string | undefined
  isAuthorized?: boolean
  addDialog?: typeof dialogActions.addDialog
  toggleCollectionBookmark?: typeof apiActions.collections.toggleCollectionBookmark
}

const CollectionCard: React.FC<CollectionCardProps> = ({
  collection,
  onClick = emptyFunction,
  to,
  variant,
  addDialog,
  currentUserId,
  toggleCollectionBookmark,
  isAuthorized
}) => {
  const isBookmarked = Boolean(collection?.bookmark)
  const { id, is_private, username, count, thumbnail } = collection
  const isCollectionOwner = _toNumber(currentUserId) === _toNumber(collection?.user?.id)
  const user = collection?.user

  const handleClick = useCallback(() => {
    if (currentUserId) {
      onClick?.(id)
    } else {
      addDialog?.({
        [InformationDialog.LOGIN_REQUIRED]: { dialogName: InformationDialog.LOGIN_REQUIRED }
      })
    }
  }, [onClick, addDialog, id, currentUserId])

  const draftPrefix = collection?.is_draft && collection?.name ? 'Draft: ' : ''
  const collectionName = `${draftPrefix}${collection?.name || nameConstant.DRAFT_COLLECTION_NAME}`

  const tags = collection?.tags ?? EMPTY_TAG_ARRAY

  const tagsComponent = useMemo(() => {
    const showedTags = _slice(tags, 0, 3)
    return showedTags.map((value, index) => (
      <Grid item key={index} title={value}>
        <Typography className={styles.CollectionCardTag} variant="body2" component="div" noWrap>
          {value}
        </Typography>
      </Grid>
    ))
  }, [tags])

  return (
    <div
      className={clsx(
        'relative block h-full w-full text-left',
        styles.CollectionCard,
        (onClick || to) && styles.CollectionCardWithHover
      )}
    >
      <CollectionCardContent
        addDialog={addDialog}
        collectionThumbnail={thumbnail?.file}
        collectionId={id}
        collectionName={collectionName}
        collectionTotal={count}
        currentUserId={currentUserId}
        isAuthorized={isAuthorized}
        isBookmarked={isBookmarked}
        isCollectionOwner={isCollectionOwner}
        isPrivate={is_private}
        onClick={handleClick}
        TagsComponent={tagsComponent}
        toggleCollectionBookmark={toggleCollectionBookmark}
        username={username}
        userUrl={UrlUtils.getUserProfileUrl(user)}
        variant={variant}
      />
    </div>
  )
}

export default CollectionCard
